import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Spinner } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { isApiError } from '@shared/api';
import { useLogin } from '../model/use-login';

const schema = z.object({
  identity: z.string().email(),
  password: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

export function LoginForm() {
  const { t } = useTranslation();
  const login = useLogin();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit((values) => login.mutate(values));

  const serverError = login.error
    ? isApiError(login.error) && login.error.status === 401
      ? t('auth.errors.invalidCredentials')
      : t('auth.errors.generic')
    : null;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <FormField
        label={t('auth.login.email')}
        htmlFor="identity"
        error={errors.identity?.message}
      >
        <Input id="identity" type="email" autoComplete="email" {...register('identity')} />
      </FormField>
      <FormField
        label={t('auth.login.password')}
        htmlFor="password"
        error={errors.password?.message}
      >
        <Input id="password" type="password" autoComplete="current-password" {...register('password')} />
      </FormField>
      {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}
      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? <Spinner /> : t('auth.login.submit')}
      </Button>
      <div className="text-center text-sm">
        <Link to={ROUTES.forgotPassword} className="text-primary hover:underline">
          {t('auth.login.forgot')}
        </Link>
      </div>
    </form>
  );
}
